import * as React from "react";
import styles from "./Card.module.css";

export type CardVariant = "elevated" | "flat";

export interface CardProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Visual treatment.
   * - `elevated` (default): the app card. A `--surface` fill, a 1px
   *   `--line` border and `--radius-lg`, with no shadow.
   * - `flat`: the same card with 16px padding. Use for in-page editor
   *   surfaces (form sections, tables, predicate groups).
   */
  variant?: CardVariant;
  /**
   * Element tag to render. Defaults to `"div"`. Use `"section"` / `"article"`
   * to preserve landmark semantics when the card is a meaningful page region.
   */
  as?: "div" | "section" | "article" | "aside";
}

const Card = React.forwardRef<HTMLElement, CardProps>(
  ({ className, variant = "elevated", as: Tag = "div", ...props }, ref) => {
    const variantClass = variant === "flat" ? styles.cardFlat : styles.card;
    return React.createElement(Tag, {
      ref,
      className: `${variantClass}${className ? ` ${className}` : ""}`,
      ...props,
    });
  },
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={`${styles.header}${className ? ` ${className}` : ''}`}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={`${styles.title}${className ? ` ${className}` : ''}`}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={`${styles.description}${className ? ` ${className}` : ''}`}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={`${styles.content}${className ? ` ${className}` : ''}`}
    {...props}
  />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={`${styles.footer}${className ? ` ${className}` : ''}`}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
};
